import React, { useEffect, useRef, useState } from 'react';

import {
  Condition,
} from "@dndModels";

import DisplayString from '@components/input/strings/DisplayString';

interface Props {
  cond: Condition;
  level: number;
  disabled: boolean;
  onChange: (level: number) => void;
  onStar: (id: string) => void;
  onImmune: (id: string) => void;
}

interface PopoverPos { top: number; left: number; }

export default function ConditionChip({
  cond,
  level,
  disabled,
  onChange,
  onStar,
  onImmune,
}: Props) {
  const [is_open, setIsOpen] = useState(false);
  const [levelPos, setLevelPos] = useState<PopoverPos>({ top: 0, left: 0 });
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!is_open) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setPopoverAnchorEl(null);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [is_open]);

  const open = (anchorEl: HTMLElement) => {
    setPopoverAnchorEl(anchorEl);
    const rect = anchorEl.getBoundingClientRect();
    setLevelPos({ top: rect.bottom + 4, left: rect.left });
    setIsOpen(true);
  };

  const handleRemoveCondition = () => {
    onChange(0);
  };

  const popoverBase: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9999,
    background: '#000',
    border: '1px solid #ccc',
    borderRadius: '6px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
  };

  const hasLevels = cond.max_level > 1;

  return (
    <div key={cond._id} style={{ display: 'inline-flex' }}>
      <div
        data-condition-popover
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 4px 2px 8px',
          borderRadius: '12px',
          background: '#e3f2fd',
          border: '1px solid #90caf9',
          fontSize: '12px',
          color: '#0d47a1',
          whiteSpace: 'nowrap',
        }}
      >
        <span onClick={e => {
            if (!disabled) {
              open(e.currentTarget as HTMLElement);
            }
          }}
          style={{ cursor: !disabled ? 'pointer' : 'default' }}>
          {cond.use_name}
          {hasLevels && (
            <span style={{
              marginLeft: '4px',
              background: '#1976d2',
              color: '#fff',
              borderRadius: '8px',
              padding: '0 5px',
              fontSize: '11px',
              fontWeight: 'bold',
            }}>
              {level}
            </span>
          )}
        </span>

        {!disabled && (
          <span
            onClick={() => handleRemoveCondition()}
            title="Remove"
            style={{
              cursor: 'pointer',
              color: '#5c8ac7',
              fontSize: '14px',
              lineHeight: 1,
              padding: '0 2px',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = '#b71c1c')}
            onMouseLeave={e => (e.currentTarget.style.color = '#5c8ac7')}
          >
            ×
          </span>
        )}
      </div>

      {is_open &&
        <div
          data-condition-popover
          ref={popoverRef}
          style={{
            ...popoverBase,
            top: levelPos.top,
            left: levelPos.left,
            padding: '8px',
            minWidth: '100px',
          }}>
          { hasLevels && 
            <>
              <div style={{ fontSize: '11px', color: '#fff', marginBottom: '6px' }}>
                Set level
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                {Array.from({ length: cond.max_level + 1 }, (_, i) => i).map(lvl => (
                  <button key={lvl}
                    onClick={() => {
                      onChange(lvl);
                      setIsOpen(false);
                    }}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      background: level === lvl ? '#1976d2' : '#f5f5f5',
                      color: level === lvl ? '#fff' : '#333',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: level === lvl ? 'bold' : 'normal',
                    }}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </>
          }
          <div style={{ fontSize: '11px', padding: "4px" }}>
            <DisplayString str={ cond.description } />
          </div>
        </div>
      }
    </div>
  );
}